import numbro from 'numbro';
import { IDictionary, ILanguage, isNullOrUndefined } from '..';
import { azAZ } from './numberVariablesLocale/az-AZ';

import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';

declare global {
	interface Window {
		supportedLanguages?: Array<ILanguage>;
		currentLanguage?: ILanguage;
		defaultLanguage?: ILanguage;
		numberVariables: numbro.NumbroLanguage;
	}
}

export class MLHelper {
	public static get supportedLanguages(): Array<ILanguage> {
		return window.supportedLanguages;
	}

	public static get currentLanguage(): ILanguage {
		return window.currentLanguage;
	}

	public static get defaultLanguage(): ILanguage {
		return window.defaultLanguage;
	}

	public static get numberVariables(): numbro.NumbroLanguage {
		return window.numberVariables;
	}

	public static readonly setSupportedLanguages = (supportedLanguages: Array<ILanguage>) => {
		window.supportedLanguages = supportedLanguages;
	};

	public static readonly setCurrentLanguage = (currentLanguage: ILanguage) => {
		window.currentLanguage = currentLanguage;
	};

	public static readonly setDefaultLanguage = (defaultLanguage: ILanguage) => {
		window.defaultLanguage = defaultLanguage;
	};

	public static async setNumberVariables(culture: string) {
		if (culture == 'en-US') {
			window.numberVariables = numbro.languageData(culture);
		} else if (culture == 'az-Latn-AZ') {
			window.numberVariables = azAZ as any;
		} else {
			window.numberVariables = require('numbro/languages/' + culture);
		}
	}

	/**Var olan mevcut bir texti ml objesine dönüştürmek için */
	public static getMLObjectWithText(text?: any, noSetCultureForNull?: boolean) {
		if (noSetCultureForNull && isNullOrUndefined(text)) {
			return {};
		}
		return typeof text === 'object'
			? text
			: { [MLHelper.currentLanguage.culture]: typeof text === 'string' ? text : '' };
	}

	public static createMLObjectWithValue(mlCode: string) {
		const result: IDictionary<string> = {};
		MLHelper.supportedLanguages.forEach(i => {
			result[i.culture] = mlCode;
		});
		return result;
	}

	/**ML olan bir objenin içeriğinden doğru texti kullanmak için */
	public static getMLText(mlText?: any) {
		if (typeof mlText === 'string') {
			return mlText;
		}
		if (typeof mlText === 'object' && mlText && !_isEmpty(mlText)) {
			if (
				MLHelper.currentLanguage &&
				!isNullOrUndefined(mlText[MLHelper.currentLanguage.culture]) &&
				mlText[MLHelper.currentLanguage.culture] !== ''
			) {
				return mlText[MLHelper.currentLanguage.culture];
			}
			if (!MLHelper.defaultLanguage && MLHelper.supportedLanguages) {
				window.defaultLanguage = window.supportedLanguages?.find(i => i.isDefault);
			}
			if (
				MLHelper.defaultLanguage &&
				!isNullOrUndefined(mlText[MLHelper.defaultLanguage.culture]) &&
				mlText[MLHelper.defaultLanguage.culture] !== ''
			) {
				return mlText[MLHelper.defaultLanguage.culture];
			}
			const hasFirstKey = Object.keys(mlText).find(
				i => !isNullOrUndefined(mlText[i]) && mlText[i] !== ''
			);
			return mlText[hasFirstKey] || '';
		}
		if (typeof mlText === 'boolean') {
			return mlText.toString();
		}
		if (typeof mlText === 'number') {
			return mlText.toString();
		}
		return '';
	}

	public static isMLObject(object: IDictionary<any>, equalExactly?: boolean) {
		if (object && typeof object === 'object') {
			const objectKeys = Object.keys(object);
			const cultureKeys = MLHelper.supportedLanguages.map(i => i.culture);

			if (equalExactly) {
				return _isEqual(
					[...objectKeys].sort((a, b) => a.localeCompare(b)),
					[...cultureKeys].sort((a, b) => a.localeCompare(b))
				);
			} else {
				return objectKeys.some(key => cultureKeys.includes(key));
			}
		}
	}

	public static getMLInfo() {
		return {
			supportedLanguages: MLHelper.supportedLanguages,
			currentLanguage: MLHelper.currentLanguage,
			defaultLanguage: MLHelper.defaultLanguage
		};
	}

	/**ML olan bir objede mevcut culturelar dolu mu kontrol ediliyor. */
	public static isMLEmpty(mlData: any): boolean {
		if (mlData) {
			if (typeof mlData === 'string') {
				return true;
			}
			if (MLHelper.supportedLanguages?.length && typeof mlData === 'object') {
				const filledLanguages = MLHelper.supportedLanguages.filter(
					language => mlData[language.culture] && mlData[language.culture] !== ''
				);
				if (filledLanguages.length) {
					return false;
				}
			}
		}
		return true;
	}

	public static getSystemCulture() {
		return this.supportedLanguages?.find(
			lang => lang.culture == localStorage.getItem('locale')
		);
	}
}
